import React, { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'

// Colour is keyed on the category, not on its position in the response.
// Positional colours shifted every category along whenever an optional one
// (Blocked, Unclassified) appeared or disappeared, so the same category
// changed colour from one period to the next. The palette also used to hold
// only six entries, so a seventh category got an undefined colour and a
// legend swatch with no background at all.
const CATEGORY_COLOURS = {
    "Store / brand search": "#BA8E27",
    "Opening hours": "#c5003e",
    "Offers / events": "#83B93F",
    "Dining": "#93C73F",
    "Parking / facilities": "#dbbb6f",
    "Wayfinding": "#826013",
    "Other": "#9AA0A6",
    "Blocked": "#5B2B84",
    "Unclassified": "#B8860B",
};

// Anything the API adds later still gets a colour rather than none.
const FALLBACK_COLOURS = ["#6C8EBF", "#4A7C59", "#8E5572", "#3F6C51"];

// Not a category the mall asked for: it means those queries could not be
// classified at all. Called out rather than blended in, because a run of
// these is a fault to investigate, not a finding to report.
const UNCLASSIFIED = "Unclassified";

const Queries = ({ commonQueries, pieName }) => {
    const [query, setquery] = useState({});

    useEffect(() => {
        setquery(commonQueries || {})
    }, [commonQueries])

    // The API answers { no_data: "<message>" } for an empty range. Feeding that
    // straight to the chart put a string where a number belongs.
    const entries = Object.entries(query || {}).filter(
        ([, value]) => typeof value === "number"
    );
    const noData = entries.length === 0;

    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    const unclassified = query[UNCLASSIFIED] || 0;

    // Zero-count categories are dropped from the pie (an empty wedge still
    // consumes a colour) but kept in the legend, so "none this month" stays
    // visible rather than looking like a missing category.
    const plotted = entries.filter(([, value]) => value > 0);

    const colourFor = (label) => {
        if (CATEGORY_COLOURS[label]) return CATEGORY_COLOURS[label];
        const unknown = entries
            .map(([key]) => key)
            .filter((key) => !CATEGORY_COLOURS[key])
            .indexOf(label);
        return FALLBACK_COLOURS[Math.max(unknown, 0) % FALLBACK_COLOURS.length];
    };

    const options = {
        chart: { type: "pie" },
        labels: plotted.map(([label]) => label),
        dataLabels: {
            enabled: false,
        },
        colors: plotted.map(([label]) => colourFor(label)),
        stroke: { width: 0 },
        legend: { show: false },
        tooltip: {
            y: {
                formatter: (value) =>
                    total > 0
                        ? `${value} (${Math.round((value / total) * 100)}%)`
                        : `${value}`,
            },
        },
    }

    return (
        <div className='shadow-custom rounded-3xl p-5 w-full lg:w-1/3'>
            <p className='text-base font-semibold text-black mb-6'>{pieName}</p>
            {noData ? (
                <div className="flex items-center justify-center h-48 text-sm text-gray-500">
                    No customer queries in this period
                </div>
            ) : (
                <div className="flex mt-7 flex-col doublexl:flex-row items-center">
                    <div className="w-full md:w-2/3">
                        <Chart
                            options={options}
                            series={plotted.map(([, value]) => value)}
                            type="pie"
                            width="100%"
                        />
                    </div>
                    <div className="flex flex-col items-start mt-4 md:mt-0 w-full doublexl:w-auto">
                        {/* Counts sit beside each label: the mall reports on how
                            many queries fell into each category, not just the
                            shape of the pie. */}
                        {entries.map(([label, value]) => (
                            <div
                                key={label}
                                className={`flex items-center gap-3 text-sm w-full ${value === 0 ? 'opacity-50' : ''}`}
                            >
                                <span
                                    className="block w-5 h-2 rounded shrink-0"
                                    style={{ backgroundColor: colourFor(label) }}
                                ></span>
                                <span className="flex-1 whitespace-nowrap">{label}</span>
                                <span className="font-semibold">{value}</span>
                            </div>
                        ))}
                        <div className="flex items-center gap-3 text-sm w-full mt-2 pt-2 border-t border-gray-200">
                            <span className="block w-5 h-2 shrink-0"></span>
                            <span className="flex-1 text-gray-600">Total</span>
                            <span className="font-semibold">{total}</span>
                        </div>
                        {unclassified > 0 && (
                            <p className="text-xs text-yellow-700 mt-3 max-w-[16rem]">
                                {unclassified} {unclassified === 1 ? "query" : "queries"} could not be
                                classified. Check that query categorisation is running.
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Queries
